import { FC, ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { createNprogress, NavigationProgress } from '@mantine/nprogress';
import '@mantine/nprogress/styles.layer.css';
import Preloader from '../Preloader';
import './ModalLoadingOverlay.scss';

export interface IModalLoadingOverlayProps {
    visible: boolean;
    children: ReactNode;
    label?: string;
    progress?: number;
    progressLabel?: string;
    /** Бегущая полоска для фоновой загрузки без измеримого процента. */
    indeterminate?: boolean;
}

const ModalLoadingOverlay: FC<IModalLoadingOverlayProps> = ({
    visible,
    children,
    label,
    progress,
    progressLabel,
    indeterminate = false,
}) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const navigationProgressRef = useRef(createNprogress());
    const [overlayTarget, setOverlayTarget] = useState<HTMLElement | null>(null);
    const [navigationProgressStore, navigationProgress] = navigationProgressRef.current;
    const hasProgress = typeof progress === 'number';
    const normalizedProgress = hasProgress ? Math.min(100, Math.max(0, Math.round(progress))) : undefined;
    const showOverlay = visible || hasProgress;
    const showIndeterminateProgress = visible && indeterminate && !hasProgress;
    const overlayLabel = progressLabel ?? label;

    const progressIndicator =
        hasProgress && normalizedProgress !== undefined ? (
            <NavigationProgress
                withinPortal={false}
                store={navigationProgressStore}
                size={3}
                className="modal-loading-overlay__navigation-progress"
            />
        ) : showIndeterminateProgress ? (
            <div
                className="modal-loading-overlay__indeterminate-progress"
                role="progressbar"
                aria-label={overlayLabel ?? 'Загрузка'}
            />
        ) : null;

    useLayoutEffect(() => {
        setOverlayTarget(containerRef.current?.closest('.rf-modal__container') as HTMLElement | null);
    }, []);

    useEffect(() => {
        if (normalizedProgress === undefined) {
            navigationProgress.reset();
            return;
        }

        navigationProgress.set(normalizedProgress);
    }, [navigationProgress, normalizedProgress]);

    useEffect(() => {
        return () => {
            navigationProgress.reset();
        };
    }, [navigationProgress]);

    return (
        <div ref={containerRef} className="modal-loading-overlay" data-loading={showOverlay || undefined}>
            {showOverlay && <div className="modal-loading-overlay__backdrop" />}
            {showOverlay &&
                (overlayTarget ? (
                    createPortal(
                        <div className="modal-loading-overlay__loader-layer">
                            <Preloader
                                size="lg"
                                position="static"
                                label={overlayLabel}
                                classNameLabel="modal-loading-overlay__label"
                            />
                        </div>,
                        overlayTarget,
                    )
                ) : (
                    <div className="modal-loading-overlay__loader-layer">
                        <Preloader
                            size="lg"
                            position="static"
                            label={overlayLabel}
                            classNameLabel="modal-loading-overlay__label"
                        />
                    </div>
                ))}
            {progressIndicator &&
                (overlayTarget ? (
                    createPortal(
                        <div className="modal-loading-overlay__progress-layer" aria-hidden={hasProgress || undefined}>
                            {progressIndicator}
                        </div>,
                        overlayTarget,
                    )
                ) : (
                    <div className="modal-loading-overlay__progress-layer" aria-hidden={hasProgress || undefined}>
                        {progressIndicator}
                    </div>
                ))}
            {children}
        </div>
    );
};

export default ModalLoadingOverlay;
